import type { Account, Labels } from '../../types/accountReceivable';

interface AccountSummaryCardProps {
  account: Account;
  labels: Labels;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function getStatusColor(percentageUsed: number): string {
  if (percentageUsed < 20) return '#22c55e';
  if (percentageUsed < 50) return '#f59e0b';
  if (percentageUsed < 70) return '#ea580c';
  return '#ef4444';
}

export function AccountSummaryCard({ account, labels }: AccountSummaryCardProps) {
  const statusColor = getStatusColor(account.percentageUsed);
  const progressWidth = Math.min(account.percentageUsed, 100);

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="mb-5 border-b border-gray-100 pb-3">
        <span className="text-base font-semibold text-gray-800">{account.name}</span>
      </div>

      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">{labels.limit}</span>
          <span className="text-xl font-semibold text-gray-800">{formatCurrency(account.limit)}</span>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">{labels.balance}</span>
          <span className="text-xl font-semibold text-red-500">{formatCurrency(account.balance)}</span>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">{labels.available}</span>
          <span className="text-xl font-semibold text-green-500">{formatCurrency(account.available)}</span>
        </div>
      </div>

      <div className="mt-2">
        <div className="h-2 overflow-hidden rounded-full bg-gray-200">
          <div
            className="h-full rounded-full transition-all duration-300"
            style={{ width: `${progressWidth}%`, backgroundColor: statusColor }}
          />
        </div>
        <div className="mt-2 flex justify-between text-[13px] text-gray-500">
          <span>{labels.used}:</span>
          <span style={{ color: statusColor }} className="font-semibold">
            {account.percentageUsed.toFixed(1)}%
          </span>
        </div>
      </div>
    </div>
  );
}
